/**
 * 참여자 페이지 공통 유틸리티
 *
 * 신청 페이지($campaignId.tsx)와 제출 페이지(submit/$campaignId.tsx)에서
 * 공유하는 날짜 포맷, 파일명 추출 등의 헬퍼 함수를 모아둔다.
 */

import type { PublicCampaignResponse } from '@/lib/seedingPublicApi'

// ==================== GCS 파일명 추출 ====================

/** GCS URL에서 원본 파일명 추출 (업로드 시 붙은 6자리 해시 제거) */
export function extractFilename(url: string): string {
  const path = decodeURIComponent(url.split('/').pop() || '')
  const match = path.match(/^(.+)_[a-f0-9]{6}\.(\w+)$/)
  return match ? `${match[1]}.${match[2]}` : path
}

// ==================== 날짜 유틸 ====================

/** UTC → KST Date 변환 헬퍼 (타임존 정보 없으면 UTC로 간주) */
export function toKSTDate(dateString: string): Date {
  return dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
    ? new Date(dateString)
    : new Date(dateString + 'Z')
}

/** 날짜 포맷 (전체: "2025년 2월 12일") */
export function formatDate(dateString: string) {
  return toKSTDate(dateString).toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/** 날짜 포맷 (간결: "2/12") - 다차수 일정 표시용 */
export function formatShortDate(dateString: string) {
  const d = toKSTDate(dateString)
  const month = d.toLocaleString('en', { timeZone: 'Asia/Seoul', month: 'numeric' })
  const day = d.toLocaleString('en', { timeZone: 'Asia/Seoul', day: 'numeric' })
  return `${month}/${day}`
}

/** 날짜 포맷 (컴팩트: "01.29") - 사이드바 일정용 */
export function formatCompactDate(dateString: string) {
  const d = toKSTDate(dateString)
  const month = d.toLocaleString('en', { timeZone: 'Asia/Seoul', month: '2-digit' })
  const day = d.toLocaleString('en', { timeZone: 'Asia/Seoul', day: '2-digit' })
  return `${month}.${day}`
}

/** 날짜+시간 포맷 (제출 내역용: "2월 12일 오후 3:00") */
export function formatDateTime(dateString: string) {
  const d = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z')
  return d.toLocaleDateString('ko-KR', {
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Seoul',
  })
}

// ==================== 캠페인 상태 ====================

/** 모집 마감 카운트다운 계산 */
export function getCountdown(campaign: PublicCampaignResponse) {
  if (campaign.status !== 'recruiting') return null
  const now = new Date()
  const end = toKSTDate(campaign.recruit_end_at)
  const diff = end.getTime() - now.getTime()
  if (diff <= 0) return null
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  if (days > 0) return `${days}일 ${hours}시간 남음`
  return `${hours}시간 남음`
}

// ==================== 쿠키 ====================

/** 쿠키에서 값 읽기 (Meta _fbc, _fbp 용) */
export function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : undefined
}
